import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import {
  CLIPBOARD_FRAGMENT_END,
  CLIPBOARD_FRAGMENT_START,
  PASTE_PARA_ATTR,
} from '@/lib/text/constants/clipboard-html-markers'
import { RUN_SPAN_ATTR, applyRunSpanStyle, readRunStyleFromSpan } from '@/lib/text/run-span'
import { readParagraphStyleAttr, writeParagraphStyleAttr } from '@/lib/text/paste-paragraph-style'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { StyledPasteRun } from './types/styled-paste-run'

/**
 * Builds an HTML snippet encoding the styled runs for the character range [start, end) from a
 * `charRunMap`. The snippet contains `data-text-run` spans (the same format used by the text-box
 * contentEditable editor) so that `parseStyledRunsFromHtml` can restore per-char styles on paste.
 *
 * `text` is the flat plain text (e.g. `textarea.value`); `charRunMap` maps each character index
 * to its source `TextRun` (null entries are paragraph separators that become bare newline nodes).
 *
 * `fonts` resolves each run's family id to a real CSS family name (WMPCM-9199). It is optional
 * only because the `data-*` attributes alone are enough for a larry→larry round trip, but omitting
 * it is close to always wrong for a copy: with no lookup `applyRunSpanStyle` emits
 * `font-family: sans-serif; font-weight: 400; font-style: normal` unconditionally, so pasting into
 * Word, Docs or an email lost the typeface, bold and italic — the outbound mirror of the inbound
 * formatting loss.
 *
 * `paragraphs` carries paragraph-level formatting — alignment, spacing, indentation, drop cap —
 * across the copy (WMPCM-10131 Issue 2). Only RUN-level style used to survive, so
 * `applyStyledInsertToParagraphs` fell back to `paragraphs[paraIdx]`, the DESTINATION's paragraph,
 * and a centred paragraph pasted left-aligned. Supplied, the source paragraph's style is written
 * onto the first span of each copied paragraph as an inert `data-*` attribute — the emitted
 * structure is otherwise byte-identical, so nothing about pasting into Word changes.
 *
 * Paragraph index is derived from the separators in `charRunMap` (a `null` entry ends a
 * paragraph), counted from 0 so a range starting mid-document still lands on the right paragraph.
 */
export function buildStyledHtmlFromRange(
  text: string,
  charRunMap: Array<TextRun | null>,
  start: number,
  end: number,
  fonts?: ParagraphBlockFontLookup,
  paragraphs?: Paragraph[]
): string {
  const container = document.createElement('div')
  // Counted from 0, not from `start`: separators before the selection still advance the index.
  let paragraphIndex = 0

  for (let scan = 0; scan < start; scan++) {
    if (charRunMap[scan] === null) paragraphIndex++
  }

  let i = start
  // The first span of each paragraph carries that paragraph's style; later spans in the same
  // paragraph must not repeat it.
  let paragraphStyleWritten = false

  while (i < end) {
    const run = charRunMap[i]

    if (run === null) {
      container.appendChild(document.createTextNode('\n'))
      paragraphIndex++
      paragraphStyleWritten = false
      i++
      continue
    }

    // Accumulate consecutive chars that share the same TextRun reference.
    let groupText = ''

    while (i < end && charRunMap[i] === run) {
      groupText += text[i]
      i++
    }

    const span = document.createElement('span')

    applyRunSpanStyle(span, run, fonts)

    if (!paragraphStyleWritten) {
      const source = paragraphs?.[paragraphIndex]

      if (source) {
        const { textLines: _textLines, ...paragraphStyle } = source

        writeParagraphStyleAttr(span, paragraphStyle)
      }

      paragraphStyleWritten = true
    }

    span.textContent = groupText
    container.appendChild(span)
  }

  return container.innerHTML
}

/**
 * Parses clipboard HTML into an ordered list of styled runs. Returns `null` when no `data-text-run`
 * spans are present — the caller should fall back to plain-text paste in that case.
 *
 * **Two producers feed this, and they encode a paragraph break differently. Both are recognised
 * here, on purpose (WMPCM-7028 AC2).**
 *
 * - `buildStyledHtmlFromRange` (a larry→larry copy) carries breaks *textually*, as bare `\n` text
 *   nodes between spans — collected below with an empty style.
 * - `parseExternalClipboardHtml` (Word, Google Docs, a web page) carries them *structurally*, as
 *   `[data-paste-para]` block wrappers, and deliberately emits no newline marker between them.
 *
 * Only the first was handled, so every block boundary from foreign HTML was silently descended
 * into and contributed nothing: `<p>A</p><p>B</p>` pasted as the single run-on paragraph `"AB"`,
 * violating AC2's "retains its format" — while the same text copied as plain text pasted
 * correctly as two paragraphs.
 *
 * Emitting a `\n` is what makes the boundary real: `applyStyledInsertToParagraphs` treats `\n` in
 * run text as its *only* paragraph separator.
 *
 * **Clipboard wrapper whitespace is stripped before any of that (WMPCM-10131 Issue 1).** The
 * browser does not hand back the bytes `execCopy` wrote: it wraps them in
 * `<html>`/`<body>`/`<!--StartFragment-->` and puts newlines around the seams. Because a bare `\n`
 * text node *is* this format's paragraph separator, that framing whitespace used to arrive as real
 * runs — a larry→larry copy of `abc` pasted as five paragraphs, `"\n\nabc\n\n"`, an empty line
 * above and below. External paste never showed it, since `parseExternalClipboardHtml` already
 * slices to the fragment and trims structurally-empty edge paragraphs; this is the same treatment,
 * so the two paths finally agree.
 *
 * A run may also carry `paraStyle` — the source paragraph's own alignment/spacing/indentation,
 * present on the first run of each copied paragraph (WMPCM-10131 Issue 2). Absent for external
 * clipboard content, which has no such attribute, so those pastes keep taking the destination's
 * paragraph settings exactly as before.
 */
export function parseStyledRunsFromHtml(html: string): StyledPasteRun[] | null {
  // Same slice `parseExternalClipboardHtml` performs, for the same reason: everything outside the
  // fragment markers is the browser's own framing, never copied content. Absent on browsers that
  // emit no markers — the edge trim below covers those, so this is an optimisation, not the fix.
  const fragmentStart = html.indexOf(CLIPBOARD_FRAGMENT_START)
  const fragmentEnd = html.indexOf(CLIPBOARD_FRAGMENT_END)
  const source =
    fragmentStart !== -1 && fragmentEnd > fragmentStart
      ? html.slice(fragmentStart + CLIPBOARD_FRAGMENT_START.length, fragmentEnd)
      : html
  // Parse into an inert document: scripts don't run, event handlers (e.g. `<img onerror>`) never
  // fire and nothing loads. Never assign clipboard HTML to `innerHTML` in the live document.
  const tmp = new DOMParser().parseFromString(source, 'text/html').body

  if (!tmp.querySelector(`[${RUN_SPAN_ATTR}]`)) return null

  const runs: StyledPasteRun[] = []
  // Separator-not-terminator: set once the first `[data-paste-para]` block is entered, so a break
  // is emitted *between* blocks and never leads the paste with a blank paragraph.
  let sawParagraphBlock = false

  const collect = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).data

      if (text) runs.push({ textContent: text, style: {} })
      return
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element

      if (el.hasAttribute(RUN_SPAN_ATTR)) {
        const text = el.textContent ?? ''
        const paraStyle = readParagraphStyleAttr(el)

        if (text) {
          runs.push({
            textContent: text,
            style: readRunStyleFromSpan(el),
            ...(paraStyle ? { paraStyle } : {}),
          })
        }

        return
      }

      // Checked inside the walk rather than over `tmp.childNodes`, so a block still registers when
      // the clipboard round trip wraps the payload (`<html><body>…`) or nests it in a container
      // div — which Chrome does, alongside its `<!--StartFragment-->` markers.
      if (el.hasAttribute(PASTE_PARA_ATTR)) {
        if (sawParagraphBlock) runs.push({ textContent: '\n', style: {} })
        sawParagraphBlock = true
      }

      for (const child of el.childNodes) collect(child)
    }
  }

  for (const child of tmp.childNodes) collect(child)

  // Drop the browser's framing whitespace from both ends.
  //
  // The discriminator is the *style object*, not the text: `readRunStyleFromSpan` unconditionally
  // assigns `underline` and `strikethrough`, so a run that came from a `data-text-run` span always
  // carries keys, while a bare text node always yields `{}`. Measured on a real two-paragraph copy
  // the key counts are `[5, 0, 5]` — the separator is the only styleless entry. Do not "simplify"
  // this to a text-only check: a span whose content is genuinely whitespace (a copied space) would
  // then be eaten too.
  //
  // Edges only, so a `\n` *between* two spans — a real paragraph the user copied — always survives.
  // The cost is that a selection deliberately starting or ending ON a separator loses that outer
  // break, which is indistinguishable from framing whitespace here. `parseExternalClipboardHtml`
  // makes the identical trade, and matching it is the point.
  const isFramingWhitespace = (run: StyledPasteRun): boolean =>
    Object.keys(run.style).length === 0 && run.textContent.trim() === ''

  let first = 0
  let end = runs.length

  while (first < end && isFramingWhitespace(runs[first]!)) first++
  while (end > first && isFramingWhitespace(runs[end - 1]!)) end--

  const trimmed = runs.slice(first, end)

  return trimmed.length > 0 ? trimmed : null
}
